<template>
  <b-nav-item-dropdown
    class="dropdown-notification mr-25"
    menu-class="dropdown-menu-media responsive"
    right
  >
    <template #button-content>
      <b-avatar
        size="38"
        variant="light"
        badge-top
        class="badge-minimal bg-transparent"
        badge-variant="danger p-25"
        :badge="totalUnreadNotificationsCount !== 0 ? totalUnreadNotificationsCount.toString() : undefined"
      >
        <GjIcon
          name="Notifications"
          class="text-dark"
          size="28"
        />
      </b-avatar>
    </template>
    <!-- Header -->
    <!-- <li class="dropdown-menu-header  d-flex align-items-center" /> -->
    <b-dropdown-header class="pb-0">
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="notification-title mb-0 mr-auto">
          {{ $tk('ClientPortal.Layout.Notifications') }}
        </h4>
        <b-link
          class="text-dark"
          @click="readAllNotifications"
        >
          {{ $tk('ClientPortal.Layout.MarkAllAsRead') }}
        </b-link>
      </div>
    </b-dropdown-header>
    <hr class="my-0 mx-1">
    <template v-if="notificationList.length < 1">
      <div class="d-flex justify-content-center align-items-center py-1">
        <GjIcon
          size="40"
          name="Notifications"
        />
        {{ $tk('ClientPortal.Layout.ThereAreNoNotifications') }}
      </div>
    </template>
    <div
      v-else
      class="dropdown-scroll-items"
    >
      <template v-if="loading">
        <b-dropdown-item
          v-for="(i,index) in 2"
          :key="index"
        >
          <b-row>
            <b-col sm="1">
              <b-skeleton type="avatar" />
            </b-col>
            <b-col class="ml-1">
              <b-skeleton
                animation="wave"
                width="40%"
              />
              <b-skeleton
                animation="wave"
                width="85%"
              />
              <b-skeleton
                animation="wave"
                width="60%"
              />
            </b-col>
          </b-row>
        </b-dropdown-item>
      </template>
      <b-dropdown-item
        v-for="notification in notificationList"
        v-else
        :key="notification.subtitle"
        @click="redirectToNotification(notification)"
      >
        <b-media v-if="notification.card != null">
          <template #aside>
            <b-avatar
              size="32"
              :src="notification.account.avatar"
              :text="notification.account.displayName"
            />
          </template>
          <p class="media-heading text-wrap">
            <span class="font-weight-bolder">
              {{ notification.card.title }}
            </span>
          </p>
          <hr>
          <p class="mb-50">
            <span class="text-muted">
              {{ $tk('ClientPortal.Layout.Description') }} :
            </span>
            {{ notification.card.description }}
          </p>
          <p
            v-for="(metaData,index) in notification.card.metadata"
            :key="index"
            class="mb-50 text-wrap"
          >
            <span class="text-muted">{{ metaData.key }}: </span>
            {{ metaData.value }}
          </p>
          <small class="text-muted">
            {{ notification.relativeDateTime }}
            <i
              v-if="notification.status == newNotificationStatus"
              class="dot ml-50"
            />
          </small>
        </b-media>
      </b-dropdown-item>
      <template v-if="infinityLoading">
        <b-dropdown-item
          v-for="(i,index) in 2"
          :key="index"
        >
          <b-row>
            <b-col sm="1">
              <b-skeleton type="avatar" />
            </b-col>
            <b-col class="ml-1">
              <b-skeleton
                animation="wave"
                width="40%"
              />
              <b-skeleton
                animation="wave"
                width="85%"
              />
              <b-skeleton
                animation="wave"
                width="60%"
              />
            </b-col>
          </b-row>
        </b-dropdown-item>
      </template>
      <InfiniteLoading
        spinner="spiral"
        @infinite="getNextNotifications"
      >
        <div slot="no-results" />
        <div slot="no-more" />
      </InfiniteLoading>
    </div>
    <hr class="my-0 mx-1">
    <b-dropdown-item
      class="text-center"
      :to="'/'+ this.$route.params.accountId + '/notifications'"
    >
      {{ $tk('ClientPortal.Layout.ReadAllNotifications') }}
      <GjIcon
        class="ml-50"
        name="ArrowRight"
      />
    </b-dropdown-item>
  </b-nav-item-dropdown>
</template>

<script>
import Ripple from 'vue-ripple-directive'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import notification from '@/mixins/toastification'
import InfiniteLoading from 'vue-infinite-loading'
import {
  mapGetters,
  mapActions,
} from 'vuex'

const NOTIFICATION_STATUS = {
  NEW: 0,
  READ: 1,
  CLICKED: 2,
  DELETED: 3,
}

export default {
  components: {
    InfiniteLoading,
    GjIcon,
  },
  directives: {
    Ripple,
  },
  mixins: [notification],
  data() {
    return {
      notifications: {},
      loading: false,
      newNotificationStatus: 0,
      page: 0,
      infinityLoading: false,
    }
  },
  computed: mapGetters(['notificationList', 'totalUnreadNotificationsCount', 'numberOfNotifications']),

  async mounted() {
    this.loading = true
    this.page = 0
    try {
      await this.getNotifications(this.page)
    } catch (error) {
      this.error(error.message)
    } finally {
      this.loading = false
    }
  },
  methods: {
    ...mapActions(['getNotifications', 'update', 'readAll']),
    async readAllNotifications() {
      try {
        this.readAll()
      } catch (error) {
        this.error(error.message)
      }
    },
    async redirectToNotification(notificationInfo) {
      if (notificationInfo.status !== NOTIFICATION_STATUS.CLICKED) {
        const response = await this.update({ notificationId: notificationInfo.id, status: NOTIFICATION_STATUS.CLICKED })
        if (response.updated) {
          this.$router.push(notificationInfo.redirectUrlSPA)
        } else {
          this.error(response.message)
        }
      } else {
        this.$router.push(notificationInfo.redirectUrlSPA)
      }
    },
    async getNextNotifications($state) {
      this.infinityLoading = true
      try {
        const { notifications } = await this.getNotifications()
        if (notifications.length === 0) {
          $state.complete()
        } else {
          $state.loaded()
        }
      } catch (error) {
        this.error(error.message)
      } finally {
        this.infinityLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
.dropdown-scroll-items {
  max-height: 450px;
  overflow-y: auto;
  .dropdown-item {
  padding: 12px 22px !important;
  &:hover {
    background-color: #F6F7F9!important;
    color: unset;
  }
  &:focus {
    background-color: transparent;
    color: unset;
  }
}
}

@media (max-width: 600px) {
  .responsive {
    max-width:400px;
  }
}
</style>
